import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Sparkles,
    CalendarDays,
    Briefcase,
    Users,
    Compass,
    TrendingUp,
    Rocket,
} from "lucide-react";
import { DEFAULT_LOCATION } from "@/lib/mockData";
import { getPublicEvents, getPublicServices } from "@/lib/entitiesStore";
import { getUsers } from "@/lib/storage";
import { EventCard } from "@/components/EventCard";
import { ServiceCard } from "@/components/ServiceCard";
import InitialAvatar from "@/components/InitialAvatar";
import { useAuth } from "@/context/AuthContext";
import {
    getFollowing,
    toggleFollow,
    isFollowing,
    getSavedServiceIds,
    getJoinedEvents,
} from "@/lib/socialStore";
import { getBookingsByBuyer } from "@/lib/bookingStore";
import { useSocialVersion } from "@/lib/hooks";

void getFollowing; // kept for future personalisation; harmless reference

const TABS = [
    { id: "for-you",  label: "For You",  icon: Sparkles },
    { id: "events",   label: "Events",   icon: CalendarDays },
    { id: "services", label: "Services", icon: Briefcase },
    { id: "people",   label: "People",   icon: Users },
    { id: "near-me",  label: "Near Me",  icon: Compass },
    { id: "trending", label: "Trending", icon: TrendingUp },
];

// Valid kinds the old route supports → map to new tab ids.
const ROUTE_TO_TAB = {
    "for-you":  "for-you",
    events:     "events",
    services:   "services",
    people:     "people",
    "near-me":  "near-me",
    trending:   "trending",
    more:       "for-you",
};

const DiscoverList = () => {
    const navigate = useNavigate();
    const { kind } = useParams();
    const { user } = useAuth();
    const v = useSocialVersion();

    const initialTab = ROUTE_TO_TAB[kind] || "for-you";
    const [tab, setTab] = useState(initialTab);

    const allEvents = useMemo(() => getPublicEvents(), [v]);
    const allServices = useMemo(() => getPublicServices(), [v]);
    const realPeople = useMemo(
        () => getUsers().filter((u) => u && u.id !== user?.id),
        [user, v]
    );

    // Personalised "For You" — mix of services & events prioritised by user
    // interests, location, and activity (saved / joined / booked history).
    const forYouFeed = useMemo(() => {
        const interests = Array.isArray(user?.interests) ? user.interests : [];
        const city = (user?.location || DEFAULT_LOCATION.city || "").toLowerCase();
        const savedIds = user ? new Set(getSavedServiceIds(user.id)) : new Set();
        const joinedIds = user ? new Set(getJoinedEvents(user.id)) : new Set();
        const bookedRaw = user ? getBookingsByBuyer(user.id) : [];
        const booked = Array.isArray(bookedRaw) ? bookedRaw : [];
        const bookedCategories = new Set(
            booked.map((b) => (b?.serviceCategory || "").toLowerCase())
        );

        const scoreService = (s) => {
            let score = 0;
            const tags = Array.isArray(s?.tags) ? s.tags : [];
            if (interests.some((t) => tags.includes(t))) score += 3;
            if (city && (s?.location || "").toLowerCase().includes(city)) score += 2;
            if (bookedCategories.has((s?.category || "").toLowerCase())) score += 2;
            if (savedIds.has(s?.id)) score += 1;
            if (s?.boosted) score += 2;
            score += Math.min(2, (s?.rating || 0) - 3);
            return score;
        };
        const scoreEvent = (e) => {
            let score = 0;
            const tags = Array.isArray(e?.tags) ? e.tags : [];
            if (interests.some((t) => tags.includes(t))) score += 3;
            if (city && (e?.location || "").toLowerCase().includes(city)) score += 2;
            if (joinedIds.has(e?.id)) score += 1;
            if (e?.boosted) score += 2;
            return score;
        };
        const items = [
            ...allServices.map((s) => ({ kind: "service", item: s, score: scoreService(s) })),
            ...allEvents.map((e) => ({ kind: "event", item: e, score: scoreEvent(e) })),
        ];
        return items.sort((a, b) => b.score - a.score);
    }, [user, v, allServices, allEvents]);

    const trendingFeed = useMemo(() => {
        // Trending = boosted first, then by reviews/attendees count, then rating.
        return [...allServices]
            .sort((a, b) => {
                const boostDiff = (b.boosted ? 1 : 0) - (a.boosted ? 1 : 0);
                if (boostDiff !== 0) return boostDiff;
                const r = (b.reviews || 0) - (a.reviews || 0);
                if (r !== 0) return r;
                return (b.rating || 0) - (a.rating || 0);
            });
    }, [allServices]);

    const nearMeFeed = useMemo(() => {
        const city = (user?.location || DEFAULT_LOCATION.city || "").toLowerCase();
        const near = (x) => (x.location || "").toLowerCase().includes(city);
        const services = allServices.filter(near);
        const events = allEvents.filter(near);
        return { services, events };
    }, [user, allServices, allEvents]);

    return (
        <div className="screen" data-testid={`discover-${tab}-screen`}>
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-2 border-b border-border/60">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="discover-back-btn"
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95 transition-[transform,background-color] duration-150"
                        aria-label="Back"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex-1">
                        <h1 className="font-display text-[16px] font-semibold">Discover</h1>
                        <p className="text-[11.5px] text-muted-foreground">
                            Feeds tuned to your interests & location
                        </p>
                    </div>
                </div>

                {/* Tab row */}
                <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="discover-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`discover-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={[
                                    "shrink-0 h-9 px-3 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 border transition-colors",
                                    active
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-secondary text-secondary-foreground border-border hover:bg-muted",
                                ].join(" ")}
                            >
                                <Icon size={12} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar p-4 pb-10">
                {tab === "for-you" && (
                    <div className="space-y-3" data-testid="discover-for-you-feed">
                        {forYouFeed.length === 0 ? (
                            <Empty text="Your personalised feed will appear here." />
                        ) : forYouFeed.map((row) =>
                            row.kind === "service" ? (
                                <ServiceRow key={"s" + row.item.id} service={row.item} />
                            ) : (
                                <EventCard key={"e" + row.item.id} event={row.item} />
                            )
                        )}
                    </div>
                )}

                {tab === "events" && (
                    <div className="space-y-3" data-testid="discover-events-feed">
                        {allEvents.length === 0 ? (
                            <Empty text="No events yet. Check back soon." />
                        ) : allEvents.map((e) => <EventCard key={e.id} event={e} />)}
                    </div>
                )}

                {tab === "services" && (
                    <div className="space-y-3" data-testid="discover-services-feed">
                        {allServices.length === 0 ? (
                            <Empty text="No services yet." />
                        ) : allServices.map((s) => <ServiceRow key={s.id} service={s} />)}
                    </div>
                )}

                {tab === "people" && (
                    <ul className="space-y-2" data-testid="discover-people-feed">
                        {realPeople.length === 0 ? (
                            <Empty text="No data available" />
                        ) : realPeople.map((p) => (
                            <PersonRow
                                key={p.id}
                                person={p}
                                meId={user?.id}
                                following={user ? isFollowing(user.id, p.id) : false}
                                onToggle={() => user && toggleFollow(user.id, p.id)}
                                onOpen={() => navigate(`/u/${p.username}`)}
                            />
                        ))}
                    </ul>
                )}

                {tab === "near-me" && (
                    <div className="space-y-3" data-testid="discover-near-me-feed">
                        {nearMeFeed.services.length === 0 && nearMeFeed.events.length === 0 ? (
                            <Empty text={`Nothing near ${user?.location || DEFAULT_LOCATION.city} yet.`} />
                        ) : (
                            <>
                                {nearMeFeed.services.map((s) => <ServiceRow key={"s" + s.id} service={s} />)}
                                {nearMeFeed.events.map((e) => <EventCard key={"e" + e.id} event={e} />)}
                            </>
                        )}
                    </div>
                )}

                {tab === "trending" && (
                    <div className="space-y-3" data-testid="discover-trending-feed">
                        {trendingFeed.length === 0 ? (
                            <Empty text="No trending listings yet." />
                        ) : trendingFeed.map((s) => <ServiceRow key={s.id} service={s} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ---------------- Rows ---------------- */

const ServiceRow = ({ service }) => {
    const navigate = useNavigate();
    return (
        <button
            type="button"
            data-testid={`discover-service-${service.id}`}
            onClick={() => navigate(`/services/${service.id}`)}
            className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/30 active:scale-[0.99] transition-[transform,background-color] text-left"
        >
            {service.image ? (
                <img src={service.image} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" />
            ) : (
                <div className="h-16 w-16 rounded-xl bg-muted shrink-0" />
            )}
            <div className="min-w-0 flex-1">
                <div className="font-display text-[14px] font-semibold truncate inline-flex items-center gap-1.5">
                    {service.name}
                    {service.boosted && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[9.5px] font-semibold">
                            <Rocket size={8} />
                            Boosted
                        </span>
                    )}
                </div>
                <div className="text-[11.5px] text-muted-foreground truncate">
                    {service.category} · {service.location}
                </div>
                {(service.rating || 0) > 0 && (
                    <div className="mt-0.5 text-[11.5px]">
                        <span className="text-amber-500">★</span> {service.rating}
                        {service.reviews ? <span className="text-muted-foreground"> ({service.reviews})</span> : null}
                    </div>
                )}
            </div>
            <span className="font-display text-[13px] font-semibold shrink-0">
                {service.priceFrom ? `From ₦${Number(service.priceFrom).toLocaleString()}` : service.price ? `₦${Number(service.price).toLocaleString()}` : ""}
            </span>
        </button>
    );
};

const PersonRow = ({ person, meId, following, onToggle, onOpen }) => {
    const name = person?.fullName || person?.name || person?.username || "Someone";
    const username = person?.username || "user";
    const location = person?.location || "Location unavailable";
    return (
        <li>
            <div
                data-testid={`discover-person-${person?.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/30 transition-colors text-left"
            >
                <button
                    onClick={onOpen}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={`discover-person-open-${person?.id}`}
                >
                    {person?.avatar ? (
                        <img src={person.avatar} alt={name} className="h-11 w-11 rounded-full object-cover ring-1 ring-border" />
                    ) : (
                        <InitialAvatar name={name} size={44} />
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-display text-[14px] font-semibold truncate">{name}</div>
                        <div className="text-[12px] text-muted-foreground truncate">
                            @{username} · {location}
                        </div>
                    </div>
                </button>
                {meId && person?.id && meId !== person.id && (
                    <button
                        data-testid={`discover-person-follow-${person.id}`}
                        onClick={onToggle}
                        className={[
                            "h-9 px-3 rounded-full text-[12px] font-medium border transition-colors",
                            following
                                ? "bg-secondary text-foreground border-border hover:bg-muted"
                                : "bg-primary text-primary-foreground border-primary",
                        ].join(" ")}
                    >
                        {following ? "Following" : "Follow"}
                    </button>
                )}
            </div>
        </li>
    );
};

const Empty = ({ text }) => (
    <div className="py-16 text-center" data-testid="discover-empty">
        <p className="text-[13px] text-muted-foreground">{text}</p>
    </div>
);

export default DiscoverList;
